// The Validate (Flows 1-2) message catalog - the same scoped mechanism
// `src/copy/certify.ts` established for Slice 4 (read in full before editing this file).
// SCOPED, ENGLISH-ONLY, LOCAL TO apps/web - flagged, not a platform i18n system. No
// message-catalog/i18n mechanism exists anywhere in this repo yet (verified: no
// i18next/useTranslation/ICU import anywhere under apps/web or docs, same finding
// certify.ts's own header already recorded) - this is the minimal mechanism that
// satisfies "render from a catalog, never a hardcoded string" without inventing the
// real, locale-routed ICU MessageFormat system that is the i18n-engineer's lane
// (`i18n-localization`). Plain `{token}` substitution only, no ICU plural rules.
//
// SOURCE OF EVERY STRING BELOW: `docs/phase-0/slice5-ux-flows.md` Flows 1-2's own copy
// shells (cited per line below), reproduced VERBATIM where the spec gives an exact
// string. Every one of those is explicitly `[copy: flagged for review]` in that spec -
// DRAFT, never final, rendered per the spec's own instruction ("render it, but it is
// not final"; `CLAUDE.md` §8/§6.6 - anything legal/domain-sensitive additionally needs
// expert review before ship). A handful of purely STRUCTURAL strings the spec's tables
// do not spell out verbatim (chat composer label, section headings, confirm-dialog
// button labels, the three illustrative "starting point" chips) are this lane's own
// minimal, plain-language shells, following the identical "additional structural
// strings this build needs, same DRAFT/flagged status" convention certify.ts's own
// tail section already sets - never a legal/compliance/figure claim, so none carries
// its own extra flag beyond this file's own header.
export const VALIDATE_COPY = {
  // ---- Flow 1: Talk-to-Glim chat (slice5-ux-flows.md §4.1-4.2) ----
  "validate.glim.heading": "Talk to Glim",
  "validate.glim.emptyPrompt": "Tell me what you're building - even one line is enough to start.",
  "validate.glim.example1": "A subscription box for coffee lovers",
  "validate.glim.example2": "A tool that tracks gym attendance",
  "validate.glim.example3": "A marketplace for local tutors",
  "validate.glim.thinking": "Glim is thinking...",
  "validate.glim.notReadyPrefix": "A couple more things first: {gap}.",
  "validate.glim.sendError": "That didn't send. Try again.",
  "validate.glim.gettingVerdict": "Getting your verdict...",
  "validate.glim.getVerdictCta": "Get my verdict",
  "validate.glim.addContextCta": "Add context",
  "validate.glim.composerLabel": "Tell Glim about your idea",
  "validate.glim.sendCta": "Send",
  "validate.glim.retryCta": "Retry",
  "validate.glim.deniedNotOwnerAdmin":
    "Ask an owner or admin on this account, or ask them to make you an admin.",
  "validate.glim.loadFailed": "We couldn't load your conversation with Glim. Try again.",

  // ---- Flow 2: hardened Validate context upload (slice5-ux-flows.md §5.2) ----
  "validate.upload.heading": "Add context to ground your verdict",
  "validate.upload.privacyNotice":
    "Stored privately and encrypted. Only your crew sees it, and only to ground your verdict.",
  "validate.upload.dropInstruction": "Drop a file here, or choose one from your computer.",
  "validate.upload.dragOver": "Drop to add",
  "validate.upload.chooseFileCta": "Choose a file",
  "validate.upload.addCta": "Add file",
  "validate.upload.wrongType": "We can only accept {types} up to {size}. Try a different file.",
  "validate.upload.uploading": "Adding {filename}...",
  "validate.upload.transferFailed":
    "The transfer didn't complete. Nothing was stored - try again.",
  "validate.upload.scanning": "Checking this file before your crew sees it...",
  "validate.upload.rejectedMalware":
    "We couldn't accept that file - it didn't pass our safety check. Nothing reached your crew. Try a different file.",
  "validate.upload.rejectedStructure":
    "We couldn't open that file safely. Try re-exporting it, or a different format.",
  "validate.upload.rejectedContent":
    "We couldn't accept that file's content. If this looks wrong, contact us.",
  "validate.upload.accepted": "{filename} added. Your crew will use it to ground your verdict.",
  "validate.upload.doneCta": "Done",
  "validate.upload.deniedNotOwnerAdmin": "Only an owner or admin can add context.",
  "validate.upload.retryCta": "Try again",
  /** Distinct from `retryCta` ("Try again", which resets to idle for a NEW file choice
   * after a content/scan rejection): this one re-sends the SAME already-selected file
   * on the SAME POST after a transfer/infra failure - a different action, so it needs
   * its own label a screen-reader user (or this test suite) can tell apart. */
  "validate.upload.retrySameCta": "Retry",

  // ---- Flow 1: the verdict pack (slice5-ux-flows.md §4.3-4.4) ----
  "validate.verdict.heading": "Your verdict",
  "validate.verdict.researching":
    "Your crew is researching your idea - market size, competitors, demand signals, a financial plan. This can take a few minutes.",
  "validate.verdict.researchFailed":
    "We couldn't finish your research. Try again - nothing about your idea was lost.",
  "validate.verdict.retryCta": "Retry",
  "validate.verdict.abstainIntro": "I don't have enough grounded to give you a real verdict yet.",
  "validate.verdict.whatIChecked": "What I checked",
  "validate.verdict.passedHeadline": "Your idea checks out. Here's why.",
  "validate.verdict.conditionalHeadline":
    "This needs some work before I'd call it validated. Here's why, and here's what would help.",
  "validate.verdict.continueCta": "Continue to branding",
  "validate.verdict.refineCta": "Talk it through with Glim",
  "validate.verdict.buildAnywayCta": "Build it - flagged as not yet validated",
  "validate.verdict.dropCta": "Drop this idea",
  "validate.verdict.buildAnywayConfirmBody":
    "Build {appName} anyway? It will be flagged as not yet validated until it earns a pass.",
  "validate.verdict.buildAnywayConfirmCta": "Build anyway",
  "validate.verdict.dropConfirmBody": "Drop this idea? Nothing is saved.",
  "validate.verdict.dropConfirmCta": "Drop it",
  "validate.verdict.cancelCta": "Cancel",
  "validate.verdict.seeEvidenceCta": "See the evidence",
  "validate.verdict.modeledEstimateTag": "Modeled estimate",
  "validate.verdict.sourceLabel": "Source: {name} · {date}",
  "validate.verdict.continueConfirmPassed": "Your idea is validated. Let's build your brand.",
  "validate.verdict.continueConfirmBuildAnyway":
    "{appName} will build as not yet validated. Let's build your brand.",
  "validate.verdict.continueButtonCta": "Continue",
  "validate.verdict.dropFailed": "That didn't go through. Try again - nothing was dropped.",
  "validate.verdict.dropSuccess": "Dropped. Nothing was saved.",
  "validate.verdict.section.market_size": "Market",
  "validate.verdict.section.competitors": "Competitors",
  "validate.verdict.section.demand": "Demand",
  "validate.verdict.section.financial_plan": "Financial plan",

  // ---- The evidence drawer (slice5-ux-flows.md §4.3) ----
  "validate.evidence.heading": "See the evidence",
  "validate.evidence.methodology": "What we checked",
  "validate.evidence.assuming": "Assuming",
  "validate.evidence.loadFailed": "We couldn't load the evidence for this claim. Try again.",
  "validate.evidence.closeCta": "Close",

  // ---- NotYetValidatedDisclosure (slice5-ux-flows.md §4.5, D46-style structural note) ----
  "validate.notYetValidated.heading": "Not yet validated",
  "validate.notYetValidated.body":
    "{appName} is building before it earned a passing verdict. Nightcrew flags this everywhere the app is shown until it re-runs Validate and passes.",
} as const;

export type ValidateCopyKey = keyof typeof VALIDATE_COPY;

/**
 * Plain `{token}` substitution only - no ICU plural rules (same scoping note as
 * `certifyCopy`). A missing var leaves the literal `{token}` in place rather than
 * throwing, so a caller mistake is visible in the render instead of a crash.
 */
export const validateCopy = (
  key: ValidateCopyKey,
  vars?: Record<string, string | number>,
): string => {
  const template = VALIDATE_COPY[key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (match: string, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
};
